// Composable for showing a custom context menu with markdown formatting options
import { ref, computed, onUnmounted } from 'vue'

export function useMarkdownContextMenu(textareaRef) {
  const showMenu = ref(false)
  const menuPosition = ref({ x: 0, y: 0 })
  const selectedText = ref('')
  const activeTextarea = ref(null)

  // Computed property to check if there's a selection
  const hasSelection = computed(() => selectedText.value.length > 0)

  const getTextarea = () => {
    // Use stored active textarea if available, otherwise fall back to ref
    return activeTextarea.value || textareaRef.value
  }

  const formatSelection = (before, after, placeholder = '') => {
    const textarea = getTextarea()
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const text = textarea.value.substring(start, end)
    const beforeText = textarea.value.substring(0, start)
    const afterText = textarea.value.substring(end)

    // If no selection, insert placeholder text with formatting
    const textToFormat = text || placeholder

    // Apply formatting
    const formattedText = `${before}${textToFormat}${after}`

    // Calculate new cursor position
    let newCursorPos = start + before.length + textToFormat.length + after.length

    // If there was no selection, position cursor in the middle of the placeholder
    if (!text && placeholder) {
      newCursorPos = start + before.length + placeholder.length
    }

    // Update textarea value and trigger v-model update
    updateTextareaValue(textarea, beforeText + formattedText + afterText, newCursorPos)
    hideMenu()
  }

  const updateTextareaValue = (textarea, newValue, cursorPos) => {
    textarea.value = newValue

    // Trigger input event to update v-model binding
    const inputEvent = new InputEvent('input', {
      bubbles: true,
      cancelable: true,
      inputType: 'insertText',
    })
    textarea.dispatchEvent(inputEvent)

    const changeEvent = new Event('change', { bubbles: true })
    textarea.dispatchEvent(changeEvent)

    setTimeout(() => {
      if (cursorPos !== undefined) {
        textarea.setSelectionRange(cursorPos, cursorPos)
      }
      textarea.focus()
    }, 0)
  }

  const formatBold = () => formatSelection('*', '*', 'bold text')
  const formatItalic = () => formatSelection('_', '_', 'italic text')
  const formatCode = () => formatSelection('`', '`', 'code')
  const formatStrikethrough = () => formatSelection('~', '~', 'strikethrough text')
  const formatUnderline = () => formatSelection('__', '__', 'underlined text')
  const formatMonospace = () => formatSelection('```\n', '\n```', 'code block')
  const formatSpoiler = () => formatSelection('||', '||', 'spoiler text')

  const formatLink = () => {
    const textarea = getTextarea()
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selectedText = textarea.value.substring(start, end)
    const beforeText = textarea.value.substring(0, start)
    const afterText = textarea.value.substring(end)

    let linkText
    let urlStart

    if (selectedText) {
      linkText = `[${selectedText}](https://url)`
      urlStart = start + linkText.indexOf('https://') + 8 // Position after 'https://'
    } else {
      linkText = '[link text](https://url)'
      urlStart = start + linkText.indexOf('https://') + 8 // Position after 'https://'
    }

    updateTextareaValue(textarea, beforeText + linkText + afterText, urlStart)
    setTimeout(() => {
      // Select 'url' part for easy replacement
      textarea.setSelectionRange(urlStart, urlStart + 3)
    }, 10)
    hideMenu()
  }

  // Helper function to ensure URL has a schema (auto-add https:// if missing)
  // This processes all links in the textarea and fixes URLs without schemas
  const ensureUrlSchemas = () => {
    const textarea = getTextarea()
    if (!textarea) return

    let text = textarea.value
    // Find all link patterns [text](url) and ensure URLs have schemas
    text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, linkText, url) => {
      // Check if URL already has a schema (http://, https://, mailto:, etc.)
      const hasSchema = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(url.trim())
      if (!hasSchema && url.trim()) {
        return `[${linkText}](https://${url.trim()})`
      }
      return match
    })

    if (text !== textarea.value) {
      const cursorPos = textarea.selectionStart
      updateTextareaValue(textarea, text, cursorPos)
    }
  }

  const formatQuotation = () => {
    const textarea = getTextarea()
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selectedText = textarea.value.substring(start, end)
    const beforeText = textarea.value.substring(0, start)
    const afterText = textarea.value.substring(end)

    // For quotation, use > prefix (Telegram-style blockquote)
    // If multi-line, add > to each line
    let formattedText
    if (selectedText) {
      const lines = selectedText.split('\n')
      formattedText = lines.map(line => (line ? `> ${line}` : '')).join('\n')
    } else {
      formattedText = '> quotation text'
    }

    const newValue = beforeText + formattedText + afterText
    const newCursorPos = start + formattedText.length
    updateTextareaValue(textarea, newValue, newCursorPos)
    hideMenu()
  }

  let lastContextMenuTime = 0

  const handleContextMenu = event => {
    // Prevent default browser context menu - this must be first
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()

    // Use event.currentTarget (the element with the listener) or event.target
    const textarea = event.currentTarget || event.target
    if (!textarea || textarea.tagName !== 'TEXTAREA') {
      hideMenu()
      return
    }

    // Store the active textarea for formatting operations
    activeTextarea.value = textarea

    // Record the time of contextmenu event
    lastContextMenuTime = Date.now()

    // Always show menu when right-clicking on textarea
    // Get selection info
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    selectedText.value = textarea.value.substring(start, end)

    const hasTextSelected = start !== end

    // Estimate menu height based on whether there's a selection
    const menuWidth = 220
    const menuMaxHeight = hasTextSelected ? 420 : 350
    const menuGap = 8

    let x = event.clientX || 0
    let y = event.clientY || 0

    // Only proceed if we have valid coordinates
    if (x === null || x === undefined || y === null || y === undefined) {
      hideMenu()
      return
    }

    // Calculate available space above and below the click point
    const spaceBelow = window.innerHeight - y - menuGap
    const spaceAbove = y - menuGap

    // Decide whether to position above or below
    const positionAbove = spaceBelow < menuMaxHeight && spaceAbove > spaceBelow

    if (positionAbove) {
      // Position above: y is where the bottom of the menu should be
      y = y - menuGap
    } else {
      // Position below: y is where the top of the menu should be
      y = y + menuGap
    }

    // Ensure menu stays within viewport horizontally
    x = Math.max(8, Math.min(x, window.innerWidth - menuWidth - 8))

    menuPosition.value = { x, y, positionAbove }
    showMenu.value = true
  }

  const hideMenu = () => {
    showMenu.value = false
    // Clear active textarea after a short delay to allow formatting operations to complete
    setTimeout(() => {
      activeTextarea.value = null
    }, 100)
  }

  const handleClickOutside = event => {
    // Ignore clicks that happen immediately after contextmenu (likely the right-click itself)
    const timeSinceContextMenu = Date.now() - lastContextMenuTime
    if (timeSinceContextMenu < 300) {
      return
    }

    // Hide menu if clicking outside of it (including on the textarea)
    if (showMenu.value && !event.target.closest('.markdown-context-menu')) {
      hideMenu()
    }
  }

  // Close menu on scroll (prevents menu from being disconnected from textarea)
  const handleScroll = () => {
    if (showMenu.value) {
      hideMenu()
    }
  }

  // Set up document-level listeners
  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside, true)
    window.removeEventListener('scroll', handleScroll, true)
  })

  // Initialize document listeners
  document.addEventListener('click', handleClickOutside, true)
  window.addEventListener('scroll', handleScroll, true)

  // Clipboard operations
  const cut = () => {
    const textarea = getTextarea()
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selectedText = textarea.value.substring(start, end)

    if (selectedText) {
      navigator.clipboard
        .writeText(selectedText)
        .then(() => {
          const beforeText = textarea.value.substring(0, start)
          const afterText = textarea.value.substring(end)
          updateTextareaValue(textarea, beforeText + afterText, start)
          hideMenu()
        })
        .catch(() => {
          // Fallback for older browsers
          document.execCommand('cut')
          hideMenu()
        })
    }
  }

  const copy = () => {
    const textarea = getTextarea()
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selectedText = textarea.value.substring(start, end)

    if (selectedText) {
      navigator.clipboard
        .writeText(selectedText)
        .then(() => {
          hideMenu()
        })
        .catch(() => {
          // Fallback for older browsers
          document.execCommand('copy')
          hideMenu()
        })
    }
  }

  const deleteText = () => {
    const textarea = getTextarea()
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const beforeText = textarea.value.substring(0, start)
    const afterText = textarea.value.substring(end)
    updateTextareaValue(textarea, beforeText + afterText, start)
    hideMenu()
  }

  return {
    showMenu,
    menuPosition,
    selectedText,
    hasSelection,
    formatBold,
    formatItalic,
    formatCode,
    formatStrikethrough,
    formatUnderline,
    formatMonospace,
    formatSpoiler,
    formatLink,
    formatQuotation,
    cut,
    copy,
    deleteText,
    handleContextMenu,
    hideMenu,
  }
}
